import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { businessTimezoneOf } from './businessTimezone';
import { useLocation, useNavigate } from 'react-router-dom';
import { getAccessibleBusinesses } from '../api';
import { useAuth } from './AuthContext';

export type BusinessItem = Record<string, unknown> & {
  id?: string | number;
  businessId?: string | number;
  businessName?: string;
  name?: string;
  title?: string;
  city?: string;
  country?: string;
};

const LOCAL_STORAGE_BUSINESS_KEY = 'selectedBusinessId';

const tryExtractBusinessArray = (
  value: unknown,
  visited = new WeakSet<object>(),
): BusinessItem[] | undefined => {
  if (Array.isArray(value)) return value;
  if (!value || typeof value !== 'object') return undefined;
  const object = value as object;
  if (visited.has(object)) return undefined;
  visited.add(object);
  const record = value as Record<string, unknown>;
  const fields = ['businesses', 'items', 'result', 'data'];
  for (const field of fields) {
    if (record[field] !== undefined) {
      const extracted = tryExtractBusinessArray(record[field], visited);
      if (Array.isArray(extracted)) return extracted;
    }
  }
  return undefined;
};

const resolveBusinessList = (payload: unknown): BusinessItem[] =>
  tryExtractBusinessArray(payload) ?? [];

interface IBusinessContext {
  businesses: BusinessItem[];
  loading: boolean;
  error: string | null;
  selectedBusinessId: string | null;
  refreshBusinesses: () => Promise<void>;
  selectBusinessId: (id: string | null) => void;
  hasValidSelection: boolean;
}

const BusinessContext = createContext<IBusinessContext | undefined>(undefined);

export const BusinessProvider = ({
  children,
}: {
  children: ReactNode;
}): JSX.Element => {
  const { hasBookingAdmin, authenticated } = useAuth();
  const [businesses, setBusinesses] = useState<BusinessItem[]>([]);
  // Initialise `loading = true` so that a deep-link page refresh (e.g.
  // /business/update/provider directly) doesn't briefly see a `loading=
  // false, businesses=[]` state and get redirected to /business by the
  // RequireBusinessSelection guard before the first refresh has run.
  // The guard correctly renders <CircularProgress /> while loading.
  // Without this, the initial render shows loading=false → guard reads
  // businesses.length===0 → Navigate to /business → race lost.
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedBusinessId, setSelectedBusinessId] = useState<string | null>(
    () => localStorage.getItem(LOCAL_STORAGE_BUSINESS_KEY),
  );

  const selectBusinessId = useCallback((id: string | null) => {
    if (!id) {
      localStorage.removeItem(LOCAL_STORAGE_BUSINESS_KEY);
      setSelectedBusinessId(null);
      return;
    }
    localStorage.setItem(LOCAL_STORAGE_BUSINESS_KEY, id);
    setSelectedBusinessId(id);
  }, []);

  const refreshBusinesses = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getAccessibleBusinesses();
      const list = resolveBusinessList(response);
      setBusinesses(list);
      // Auto-select the first business synchronously in the same render batch
      // as setBusinesses + setLoading(false), so the next render is never
      // (loading=false, businesses=[N], selectedBusinessId=null) — that
      // transient state makes RequireBusinessSelection redirect to /business
      // on deep-link refresh before the post-render auto-select effect can
      // run. Read localStorage live (not from state) because state may still
      // carry a stale id from a no-longer-accessible business.
      const stored = localStorage.getItem(LOCAL_STORAGE_BUSINESS_KEY);
      const accessibleStored = stored
        ? list.find((b) => String(b.id ?? b.businessId ?? '') === stored)
        : undefined;
      if (!accessibleStored && list.length > 0) {
        const first = String(list[0].id ?? list[0].businessId ?? '');
        if (first) {
          localStorage.setItem(LOCAL_STORAGE_BUSINESS_KEY, first);
          setSelectedBusinessId(first);
        }
      }
    } catch (err: unknown) {
      const message =
        err instanceof Error
          ? err.message
          : typeof err === 'string'
            ? err
            : 'Unable to load businesses';
      setError(message);
      setBusinesses([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!authenticated) return;
    void refreshBusinesses();
  }, [authenticated, refreshBusinesses]);

  /** When nothing is selected but the accessible list has items, pick the first (dashboard/calendar used to do this ad hoc). */
  useEffect(() => {
    if (loading) return;
    if (selectedBusinessId) return;
    if (businesses.length === 0) return;
    const first = String(businesses[0].id ?? businesses[0].businessId ?? '');
    if (!first) return;
    selectBusinessId(first);
  }, [loading, businesses, selectedBusinessId, selectBusinessId]);

  // Deep-link auto-activation. The chatbot's "Open booking-admin" bridge
  // links the operator into any admin URL with `?business_id=X` —
  // previously only the /business page consumed this param, so a link
  // landing on /, /service, /staff etc. left the operator on whatever
  // business they last had selected (or the auto-picked first), which
  // looks broken to chatbot users. Moving it here applies globally to
  // every route under the provider.
  //
  // Conditions: param present + accessible to the operator + not already
  // selected. We strip the param after applying so a refresh on the same
  // URL doesn't ping-pong the selection (and the operator can switch
  // away freely without the URL forcing them back).
  const location = useLocation();
  const navigate = useNavigate();
  const appliedBridgeId = useRef<string | null>(null);
  useEffect(() => {
    if (loading || businesses.length === 0) return;
    const params = new URLSearchParams(location.search);
    const bridgeId = params.get('business_id');
    if (!bridgeId || bridgeId === appliedBridgeId.current) return;
    const owned = businesses.some(
      (b) => String(b.id ?? b.businessId ?? '') === bridgeId,
    );
    if (!owned) {
      // Not in the operator's accessible list — silently ignore. We
      // don't surface an error because the chatbot may have stale
      // permission data; the operator should just see the admin with
      // their current selection, not an error toast.
      appliedBridgeId.current = bridgeId;
      return;
    }
    if (selectedBusinessId !== bridgeId) {
      selectBusinessId(bridgeId);
    }
    appliedBridgeId.current = bridgeId;
    // Strip the param so refresh doesn't re-activate; preserve the rest
    // of the URL (login_hint, other state).
    params.delete('business_id');
    const search = params.toString();
    navigate(
      { pathname: location.pathname, search: search ? `?${search}` : '', hash: location.hash },
      { replace: true },
    );
  }, [loading, businesses, location.search, location.pathname, location.hash, selectedBusinessId, selectBusinessId, navigate]);

  // Keep selection valid against current accessible businesses.
  //
  // Two guards before the validation actually runs:
  //   1. `loading` — refresh in flight; the empty `businesses` array isn't
  //      authoritative yet.
  //   2. `businesses.length === 0` after load AND `error == null` — could
  //      legitimately mean the operator has no accessible businesses (let
  //      RequireBusinessSelection's empty-state handle that), but it could
  //      also mean a transient read failure; bail rather than wipe the
  //      stored choice prematurely.
  //
  // Without these guards, full-reload boot orders like below would silently
  // revert the operator's saved choice to `list[0]`:
  //   • Lazy init reads `015d7b60` → state `015d7b60`
  //   • User info hasn't loaded → `hasBookingAdmin()` returns false
  //   • This effect runs against `businesses=[]` → not found → nulls
  //     the selection
  //   • Refresh completes, businesses=[23] → auto-pick effect picks
  //     `list[0]` (alphabetically "44"), clobbering the operator's choice.
  useEffect(() => {
    if (loading) return;
    if (businesses.length === 0) return;
    if (hasBookingAdmin()) return;
    if (!selectedBusinessId) return;
    const ok = businesses.some((b) => String(b.id ?? b.businessId ?? '') === selectedBusinessId);
    if (!ok) {
      selectBusinessId(null);
    }
  }, [loading, businesses, selectedBusinessId, selectBusinessId, hasBookingAdmin]);

  const hasValidSelection = useMemo(() => {
    if (hasBookingAdmin()) return true;
    if (!selectedBusinessId) return false;
    return businesses.some(
      (b) => String(b.id ?? b.businessId ?? '') === selectedBusinessId,
    );
  }, [businesses, selectedBusinessId, hasBookingAdmin]);

  return (
    <BusinessContext.Provider
      value={{
        businesses,
        loading,
        error,
        selectedBusinessId,
        refreshBusinesses,
        selectBusinessId,
        hasValidSelection,
      }}
    >
      {children}
    </BusinessContext.Provider>
  );
};

export const useBusinessContext = (): IBusinessContext => {
  const ctx = useContext(BusinessContext);
  if (!ctx) throw new Error('useBusinessContext must be used inside BusinessProvider');
  return ctx;
};

/** Selected business's IANA timezone (undefined until the list loads). */
export const useSelectedBusinessTimezone = (): string | undefined => {
  const { businesses, selectedBusinessId } = useBusinessContext();
  return businessTimezoneOf(businesses, selectedBusinessId);
};

